/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';

import {
  TYPE_ADDERROR,
  TYPE_DISMISS,
  TYPE_DISMISS_ALL,
  TYPE_DISMISS_TYPE,
  TYPES,
  TYPE_SET_UNREAD,
  TYPE_READ_ALL,
  TYPE_RESET,
} from 'constants/Error';
import { getHasUndismissedErrorsOfType } from 'reducers/Error';
import { getPluginLifecycleHook } from 'reducers/Plugins';
import { Alert } from 'types/Alerts';

const SentryCategory = 'ErplyAlert';

const sesStId = sessionStorage.getItem('latestAlertId');
const alerts = sessionStorage.getItem('alerts') ?? '[]';
function parseAlerts(): Alert[] {
  try {
    const parsedAlerts = JSON.parse(alerts);
    return Array.isArray(parsedAlerts) ? parsedAlerts : [];
  } catch {
    return [];
  }
}
const parsedAlerts = parseAlerts();

const maxAlert = parsedAlerts
  .map(a => a.id)
  .reduce((max, id) => Math.max(max, id), 0);
const sessId = sesStId ? Number(sesStId) : 0;

let lastId = Math.max(sessId, maxAlert);

/**
 * A promise to ensure that adding/removing errors runs in order, since they have been turned into async actions now
 */
let errorQueue = Promise.resolve();

function runWithQueue(action) {
  return function thunk(dispatch, getState) {
    errorQueue = errorQueue
      // ignore previous errors
      .catch(() => undefined)
      // Run current action
      .then(() => dispatch(action));
    return errorQueue;
  };
}

export const dismiss = id => runWithQueue({ type: TYPE_DISMISS, payload: id });
export const dismissType = type =>
  runWithQueue(async (dispatch, getState) => {
    if (getHasUndismissedErrorsOfType(type)(getState())) {
      dispatch({ type: TYPE_DISMISS_TYPE, payload: type });
    }
  });
export const dismissAll = () => runWithQueue({ type: TYPE_DISMISS_ALL });
export const reset = () => runWithQueue({ type: TYPE_RESET });

export const setUnreadNotification = (id, unread) => {
  return {
    type: TYPE_SET_UNREAD,
    payload: { id, unread },
  };
};
export const readAllNotifications = () => ({ type: TYPE_READ_ALL });

const add = (
  TYPE,
  text,
  { dismissible, selfDismiss: selfDismissParam, errorType: name }: Options,
) => async (dispatch, getState) => {
  const state = getState();

  const selfDismiss = selfDismissParam === true ? 2500 : selfDismissParam;
  try {
    let params = {
      id: 0,
      text,
      dismissible,
      selfDismiss,
      name,
      type: TYPE,
      dismissed: false,
      timestamp: Date.now(),
      unread: false,
    };
    // @ts-ignore
    const { before, on, after } = getPluginLifecycleHook('onAddAlert')(state);

    await dispatch(before(params));

    Sentry.addBreadcrumb({
      category: SentryCategory,
      level:
        ({
          [TYPES.SUCCESS]: 'info',
          [TYPES.WARNING]: 'warning',
          [TYPES.ERROR]: 'error',
        }[TYPE] as Sentry.Severity) ?? 'log',
      message: text,
      data: {
        type: 'success',
      },
    });

    lastId += 1;
    params.id = lastId;
    params = await dispatch(on(params, params));
    dispatch({
      type: TYPE_ADDERROR,
      payload: params,
    });
    dispatch(after(params, null));
    return lastId;
  } catch (e) {
    // Unexpected error or error from plugin hook
    console.error('Error displaying POS message', { e });
  }
  return lastId;
};

type Options = {
  /** @default true */
  dismissible: boolean;
  /** @default true */
  selfDismiss: number | boolean;
  errorType?: string;
};

const Options = (opt?: Partial<Options>): Options => ({
  dismissible: true,
  selfDismiss: true,
  ...opt,
});

export function addSuccess(text: string, options?: Partial<Options>) {
  return runWithQueue(add(TYPES.SUCCESS, text, Options(options)));
}
export function addWarning(text: string, options?: Partial<Options>) {
  return runWithQueue(add(TYPES.WARNING, text, Options(options)));
}
export function addError(text: string, options?: Partial<Options>) {
  return runWithQueue(add(TYPES.ERROR, text, Options(options)));
}
export function addProgress(text: string, errorType: string) {
  return runWithQueue(
    add(TYPES.WARNING, text, {
      dismissible: false,
      selfDismiss: false,
      errorType,
    }),
  );
}
